/**
 * Firebase client SDK init.
 *
 * Credenziali dalle env VITE_FIREBASE_*. In dev, opzionalmente collega agli
 * emulatori (VITE_USE_EMULATORS=true).
 *
 * Esporta DUE shape diversi per accomodare entrambi gli stili di codice:
 *   - Sprint 2 (legacy): auth(), db(), fns() — getter lazy
 *   - Sprint 3: firebaseApp, functions — oggetti diretti
 *
 * Entrambi puntano alla stessa app singleton (no double-init).
 */

import { initializeApp, getApps } from 'firebase/app';
import {
  getAuth,
  signInAnonymously,
  signInWithCustomToken,
  signOut,
  onAuthStateChanged,
  connectAuthEmulator,
} from 'firebase/auth';
import { getFirestore, initializeFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { isNativeApp } from './platform';
import {
  getFunctions,
  httpsCallable,
  connectFunctionsEmulator,
} from 'firebase/functions';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

const REGION = import.meta.env.VITE_FUNCTIONS_REGION || 'europe-west1';
const USE_EMULATORS = import.meta.env.VITE_USE_EMULATORS === 'true';

// RELAY Tor-friendly (SOLO app nativa, SOLO in modalità Tor): Google/Firebase
// risponde 403 agli IP di uscita Tor → in modalità Tor instradiamo
// Firestore/Functions/Auth/Storage verso il relay (nullum-relay-tor.netlify.app),
// che inoltra a Google da IP pulito e rimette il CORS.
// Gate: nativo + env presente + FLAG "modalità Tor" attiva. Il flag è in
// **sessionStorage** apposta: sopravvive al reload (che serve per ri-inizializzare
// Firebase quando si accende Tor) ma NON al riavvio dell'app → coerente col fatto
// che il motore Tor stesso non sopravvive al riavvio. A Tor SPENTO l'app nativa
// parla DIRETTA con Google (veloce, nessuna dipendenza dal relay); sul web idem.
export const RELAY_HOST = import.meta.env.VITE_RELAY_HOST; // es. 'nullum-relay-tor.netlify.app'
export const TOR_RELAY_FLAG = 'nullum.torRelay';
let _relayOn = false;
try { _relayOn = sessionStorage.getItem(TOR_RELAY_FLAG) === '1'; } catch { /* no-op */ }
export const useRelay = isNativeApp() && !!RELAY_HOST && !USE_EMULATORS && _relayOn;

if (!firebaseConfig.apiKey) {
  console.warn('[nullum] FIREBASE_NOT_CONFIGURED — manca VITE_FIREBASE_API_KEY nel .env');
}

const _app = getApps().length ? getApps()[0] : initializeApp(firebaseConfig);
const _auth = getAuth(_app);

// AUTH via relay: connectAuthEmulator è l'unico override host pubblico dell'SDK.
// Il relay inoltra a identitytoolkit/securetoken VERI → il client riceve JWT
// reali firmati (auto-refresh e onIdTokenChanged normali). DEVE stare PRIMA di
// qualunque sign-in. (`appVerificationDisabledForTesting` che ne consegue tocca
// solo la phone-auth/reCAPTCHA, che non usiamo.)
if (useRelay) {
  connectAuthEmulator(_auth, `https://${RELAY_HOST}`, { disableWarnings: true });
}

// Firestore: nel guscio NATIVO (WebView dietro il proxy Tor) forziamo il
// long-polling puro → il realtime viaggia su richieste HTTPS brevi (25s < 30s
// server Google < 40s deadline header Netlify). Con `useRelay` puntiamo l'host al
// relay. initializeFirestore DEVE precedere ogni getFirestore(_app).
const _lp = {
  experimentalForceLongPolling: true,
  experimentalLongPollingOptions: { timeoutSeconds: 25 },
};
const _db = useRelay
  ? initializeFirestore(_app, { host: RELAY_HOST, ssl: true, ..._lp })
  : isNativeApp()
    ? initializeFirestore(_app, _lp)
    : getFirestore(_app);

// Functions: customDomain sul relay (`https://<relay>/fn`) → ogni httpsCallable
// va a `/fn/<name>`, che il relay mappa su cloudfunctions. Copre TUTTE le
// chiamate (callFn + le httpsCallable dirette) senza toccarle una per una.
const _functions = useRelay
  ? getFunctions(_app, `https://${RELAY_HOST}/fn`)
  : getFunctions(_app, REGION);

// Storage (allegati): ripuntiamo l'host al relay SENZA connectStorageEmulator
// (che forzerebbe `_protocol='http'`). Settiamo direttamente `host` (settabile,
// è ciò che fa internamente connectStorageEmulator) e lasciamo `_protocol='https'`
// → gli URL diventano `https://<relay>/v0/b/...`, che il relay mappa su
// firebasestorage.googleapis.com. Il token auth resta quello VERO (non tocchiamo
// _overrideAuthToken). uploadBytes/getBlob passano di qui.
const _storage = getStorage(_app);
if (useRelay) {
  try { _storage.host = RELAY_HOST; _storage._protocol = 'https'; } catch { /* no-op */ }
}

let _emulatorsConnected = false;
if (USE_EMULATORS && !_emulatorsConnected) {
  try {
    connectAuthEmulator(_auth, 'http://127.0.0.1:9099', { disableWarnings: true });
    connectFirestoreEmulator(_db, '127.0.0.1', 8080);
    connectFunctionsEmulator(_functions, '127.0.0.1', 5001);
    _emulatorsConnected = true;
    console.info('[nullum] connected to Firebase emulators');
  } catch (err) {
    console.warn('[nullum] emulator connection failed', err);
  }
}

// Sprint 2 API (legacy): funzioni getter
export function getApp() { return _app; }
export function auth() { return _auth; }
export function db() { return _db; }
export function fns() { return _functions; }
export function callFn(name) { return httpsCallable(_functions, name); }

// Sprint 3 API: oggetti diretti
export const firebaseApp = _app;
export const functions = _functions;
export const firestore = _db;
export const firebaseAuth = _auth;
export const storage = _storage;

// Auth helpers re-export
export {
  signInAnonymously,
  signInWithCustomToken,
  signOut,
  onAuthStateChanged,
};
